import { type InvalidateQueryFilters, type Query, type QueryClient } from "@tanstack/react-query"

const followUpPending = new WeakSet<Query>()

/**
 * Invalidation for a live event: never restarts a fetch in flight, but invalidates that query again
 * once the fetch settles. The fetch may have read the database before the event's change, and its
 * success clears the invalidated flag, so without the second pass the change is lost until the next
 * event. Events landing during one fetch share one follow-up, which reads after all of them.
 */
export function invalidateLive(queryClient: QueryClient, filters: InvalidateQueryFilters) {
  for (const query of queryClient.getQueryCache().findAll(filters)) {
    if (query.state.fetchStatus !== "fetching" || !query.promise || followUpPending.has(query)) {
      continue
    }
    followUpPending.add(query)
    const again = () => {
      followUpPending.delete(query)
      void invalidateLive(queryClient, { queryKey: query.queryKey, exact: true })
    }
    void query.promise.then(again, again)
  }
  return queryClient.invalidateQueries(filters, { cancelRefetch: false })
}
